import { useState, useEffect, useMemo } from 'react';
import { useSourceStreams } from '../../hooks/useSource';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Loader2, AlertTriangle } from 'lucide-react';

export interface FullResyncOptions {
  selectedStreams: string[] | null;
  dropSchemaFirst: boolean;
}

interface FullResyncDialogProps {
  sourceId: string;
  // 'full' = ignore sync state and re-import (default); 'selective' = a normal
  // incremental sync limited to the chosen streams (no wipe option).
  mode?: 'full' | 'selective';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (options: FullResyncOptions) => Promise<void>;
}

export function FullResyncDialog({ sourceId, mode = 'full', open, onOpenChange, onConfirm }: FullResyncDialogProps) {
  const selective = mode === 'selective';
  const streams = useSourceStreams(sourceId, open);
  const [selectedStreams, setSelectedStreams] = useState<Set<string>>(new Set());
  const [dropSchemaFirst, setDropSchemaFirst] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (open && streams.data) {
      setSelectedStreams(new Set(streams.data));
      setDropSchemaFirst(false);
    }
  }, [open, streams.data]);

  const hasMultipleStreams = useMemo(() => {
    return streams.data && streams.data.length > 1;
  }, [streams.data]);

  const allSelected = useMemo(() => {
    return streams.data && selectedStreams.size === streams.data.length;
  }, [streams.data, selectedStreams]);

  const noneSelected = selectedStreams.size === 0;

  const toggleStream = (stream: string) => {
    setSelectedStreams((prev) => {
      const next = new Set(prev);
      if (next.has(stream)) {
        next.delete(stream);
      } else {
        next.add(stream);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedStreams(new Set());
    } else {
      setSelectedStreams(new Set(streams.data || []));
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm({
        selectedStreams: allSelected ? null : Array.from(selectedStreams),
        dropSchemaFirst: selective ? false : dropSchemaFirst,
      });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!confirming) onOpenChange(v); }}>
      <DialogContent
        hideClose={confirming}
        onInteractOutside={(e) => { if (confirming) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (confirming) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{selective ? 'Sync Selected Streams' : 'Full Re-Sync'}</DialogTitle>
          <DialogDescription>
            {selective
              ? 'Run a normal sync for only the streams you choose. Previous sync state is kept, so this picks up where the last sync left off.'
              : 'This will re-import all data from the source, ignoring any previous sync state.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4 py-2">
          {streams.isLoading && (
            <div className="flex items-center gap-2 justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Loading streams...</span>
            </div>
          )}

          {hasMultipleStreams && (
            <div className="space-y-2">
              <p className="text-sm font-medium">{selective ? 'Select streams to sync:' : 'Select streams to re-sync:'}</p>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={!!allSelected}
                  onCheckedChange={toggleAll}
                />
                <span className="text-sm">Select All</span>
              </label>
              <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-1">
                {streams.data?.map((stream) => (
                  <label key={stream} className="flex items-center gap-2 cursor-pointer px-2 py-1 hover:bg-accent/50 rounded-sm">
                    <Checkbox
                      checked={selectedStreams.has(stream)}
                      onCheckedChange={() => toggleStream(stream)}
                    />
                    <span className="text-sm">{stream}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {!selective && (
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={dropSchemaFirst}
              onCheckedChange={(checked) => setDropSchemaFirst(checked === true)}
            />
            <span className="text-sm">Wipe existing data before sync</span>
          </label>
          )}

          {!selective && dropSchemaFirst && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                {allSelected || !hasMultipleStreams
                  ? 'This will delete all existing data in this source before syncing.'
                  : 'This will delete existing data for the selected stream(s) before syncing.'}
              </p>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={confirming}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={noneSelected || confirming}>
            {confirming ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {selective ? 'Syncing...' : 'Re-Syncing...'}
              </>
            ) : (
              selective ? 'Sync' : 'Re-Sync'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
